// Section for import npm components -----------------------------------------------------------------------------------
import React, { useContext, useEffect, useState } from 'react';

// MUI components
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import Checkbox from '@mui/material/Checkbox';
import CheckIcon from '@mui/icons-material/Check';

// Section for import custom components --------------------------------------------------------------------------------
import CommonUtility from '@utils/Common';
import { AppConfigContext } from '@providers/AppConfigProvider';
import { useMeta } from '@providers/MansionNavigation/MetaProvider';
import { SelectedLinesContext, useUpdateSelectedLines } from '@providers/MansionNavigation/SelectedLinesProvider';

// Section for type definition -----------------------------------------------------------------------------------------
import filterEntries from '@components/MansionNavigation/Utils/Filters';
import generateSortQuery from '@components/MansionNavigation/Utils/Sort';
import CustomCheckboxEvent from '@/types/event/CustomCheckboxEvent';
import { LineApiData } from '@/types/components/MansionNavigation/LinesApiData';
import LinesListData, { LineListData } from '@/types/components/MansionNavigation/LinesListData';

type LineGroupData = LineApiData[];

const commonUtil = CommonUtility;
const linesJsonUrl: string = '/karte/api/v1/lines/';

export default function LinesList(props: any) {
  const { selectedPrefecture } = props;
  const { isMobile } = useContext(AppConfigContext);
  const { meta } = useMeta();

  const filterQuery = filterEntries(meta.filters)
    ? `?${filterEntries(meta.filters)}${generateSortQuery(meta.sort, '&')}`
    : `${generateSortQuery(meta.sort, '?')}`;

  const apiQueryParameter: string = `?prefecture_code=${selectedPrefecture.pref_code_name}`;

  const selectedLines = useContext(SelectedLinesContext);
  const setSelectedLines = useUpdateSelectedLines();

  const stateLineGroupData: LineGroupData = [];
  const [lineGroupData, setLineGroupData] = useState(stateLineGroupData);

  const stateLinesData: LinesListData = [];
  const [linesData, setLinesData] = useState(stateLinesData);

  // const setSelectedStations = useSelectedStations();

  function getPreSelectedLineIds(): number[] {
    const urlParams: URLSearchParams = new URLSearchParams(window.location.search);
    const preSelectedLineIds: string | null = urlParams.get('pre_selected_line_id');
    const returnValue: string[] = preSelectedLineIds ? preSelectedLineIds.split(',') : [];
    return commonUtil.convertTypeArrayNumber(returnValue);
  }

  function grantPreSelected(citiesDataId: number, checkPreSelectedLineIds: number[]): boolean {
    let returnValue: boolean = false;
    const result = checkPreSelectedLineIds.find((preSelectedCityId: number) => preSelectedCityId === citiesDataId);

    if (result !== undefined) {
      returnValue = true;
    }
    return returnValue;
  }

  function grantSelected(checkLinesData: LinesListData, checkSelectedLineIds: number[]): void {
    if (checkSelectedLineIds.length === 0) {
      checkLinesData.map((lineData: LineListData) => {
        // eslint-disable-next-line no-param-reassign
        lineData.selected = false;
        return lineData;
      });
      return;
    }

    checkLinesData.map((lineData: LineListData) => {
      if (checkSelectedLineIds.find((checkSelectedLineId) => checkSelectedLineId === Number(lineData.id_number))) {
        // eslint-disable-next-line no-param-reassign
        lineData.selected = true;
      } else {
        // eslint-disable-next-line no-param-reassign
        lineData.selected = false;
      }
      return lineData;
    });
  }

  const getLines = (url: string) => {
    return new Promise((resolve) => {
      fetch(url)
        .then((res) => res.json())
        .then((data) => resolve(data));
    });
  };

  const loadLines = async (data: any, checkPreSelectedLineIds: number[]): Promise<void> => {
    const linesRecord: any = await Promise.all(
      data.results.map((line: LineApiData) => {
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const linesRecord: LineListData = {
          key: `lines-${commonUtil.convertNumberToString(line.id)}`,
          id: commonUtil.convertNumberToString(line.id),
          id_number: line.id,
          ekidata_jp_line_id: line.ekidata_jp_line_id,
          mrweb_line_id: line.mrweb_line_id,
          company_name: line.company_name,
          line_name: line.line_name,
          line_name_kana: line.line_name_kana,
          line_name_alphabet: line.line_name_alphabet,
          homes_rail_cd: line.homes_rail_cd,
          ekidata_jp_sort_id: line.ekidata_jp_sort_id,
          primary_sort_id: line.primary_sort_id,
          secondary_sort_id: line.secondary_sort_id,
          mansion_count: line.mansion_count,
          estate_mansion_count: line.estate_mansion_count,
          selected: grantPreSelected(line.id, checkPreSelectedLineIds),
          created_at: line.created_at,
          updated_at: line.updated_at,
        };
        // console.log(linesRecord);
        return linesRecord;
      }),
    );

    const initLineGroupData: any = Object.values(commonUtil.groupBy(linesRecord, 'company_name'));

    // console.log(linesRecord);
    // console.log(initLineGroupData);

    setLinesData(linesRecord);
    setLineGroupData(initLineGroupData);
  };

  useEffect(() => {
    const preSelectedLineIds = getPreSelectedLineIds();
    setSelectedLines(preSelectedLineIds);
    const fetchLinesData = async (): Promise<void> => {
      const data: any = await getLines(linesJsonUrl + apiQueryParameter);
      loadLines(data, preSelectedLineIds).then();
    };
    fetchLinesData().then();
  }, []);

  const handleChange = (event: CustomCheckboxEvent): void => {
    let formSelectedLinesArray: number[];
    formSelectedLinesArray = selectedLines.concat();

    if (event.target.checked) {
      formSelectedLinesArray.push(Number(event.target.id));
      setSelectedLines(formSelectedLinesArray);
      grantSelected(linesData, formSelectedLinesArray);
    } else {
      formSelectedLinesArray = selectedLines.concat();
      const mapFormSelectedCities: any = commonUtil.convertArrayToObject(formSelectedLinesArray);
      delete mapFormSelectedCities[event.target.id];

      formSelectedLinesArray = Object.values(mapFormSelectedCities);
      setSelectedLines(formSelectedLinesArray);
      grantSelected(linesData, formSelectedLinesArray);
    }
  };

  // Section for render component --------------------------------------------------------------------------------------
  return (
    <Box className="md:mx-24">
      {isMobile ? (
        // Section for render Mobile mode ----------------------------------------------------------------------------------
        <Container className="p-0">
          <List className="p-0">
            {lineGroupData.map((lineGroup: any, index: number) => (
              <ListItem key={`line-group-${index.toString()}`} className="block p-0">
                <List
                  className={`lines_list p-0 ${
                    index === lineGroupData.length - 1 ? 'mb-0' : 'mb-8'
                  } border-b-[#D9D9D9] border-b border-solid`}
                >
                  <Box className="pb-4 border-b-[#D5D5D5] border-b border-solid">
                    <Typography
                      variant="h2"
                      gutterBottom
                      className="text-lg font-bold tracking-normal mb-0 mx-5 pl-[18px] border-l-2 border-l-[#191919] border-solid"
                    >
                      {lineGroup[0].company_name}
                    </Typography>
                  </Box>
                  {linesData
                    .filter((lineData: any) => lineData.company_name === lineGroup[0].company_name)
                    .map((line: any) => (
                      <ListItem
                        key={`line_group-${lineGroup[0].id}-${line.id}`}
                        className="lines relative w-[calc(100%_-_20px)] ml-5 border-b-[#D9D9D9] border-b border-solid p-0"
                      >
                        {line.mansion_count > 0 ? (
                          <>
                            <Checkbox
                              className="lines_check mr-3 p-0"
                              onChange={handleChange}
                              id={line.id}
                              checked={line.selected}
                            />
                            <CheckIcon className="lines_check_icon hidden absolute w-5 h-5 text-white" />
                            <Link
                              href={`/karte/${selectedPrefecture.pref_code_name}/lines/${line.line_name_alphabet}/${filterQuery}`}
                              className="block w-full py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] no-underline"
                            >
                              {line.line_name}
                            </Link>
                          </>
                        ) : (
                          <>
                            <Checkbox className="lines_check opacity-80" id={line.id} disabled />
                            <CheckIcon className="lines_check_icon hidden absolute w-5 h-5 text-white" />
                            <Box className="block w-full py-[18px] text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] opacity-50 ">
                              {line.line_name}
                            </Box>
                          </>
                        )}
                      </ListItem>
                    ))}
                </List>
              </ListItem>
            ))}
          </List>
        </Container>
      ) : (
        // Section for render PC mode ----------------------------------------------------------------------------------
        <Box>
          <List className="p-0">
            {lineGroupData.map((lineGroup: any, index: number) => (
              <ListItem key={`line-group-${index.toString()}`} className="block w-full p-0 mt-6">
                <List className="flex flex-wrap justify-start p-0">
                  <Box className="w-full mb-6 pb-3 border-b-[#D5D5D5] border-b border-solid">
                    <Typography
                      variant="h2"
                      gutterBottom
                      className="text-lg font-bold leading-6 tracking-[0.08em] text-left pl-4 border-l-2 border-[#191919] mb-0"
                    >
                      {lineGroup[0].company_name}
                    </Typography>
                  </Box>
                  {linesData
                    .filter((lineData: any) => lineData.company_name === lineGroup[0].company_name)
                    .map((line: any) => (
                      <ListItem
                        key={`line_group-${lineGroup[0].id}-${line.id}`}
                        className="lines relative flex p-0 mb-6 w-1/4 self-center max-lg:w-1/3"
                      >
                        {line.mansion_count > 0 ? (
                          <>
                            <Checkbox
                              className="lines_check  mr-3 p-0"
                              onChange={handleChange}
                              id={line.id}
                              checked={line.selected}
                            />
                            <CheckIcon className="lines_check_icon hidden absolute w-5 h-5 text-white" />
                            <Link
                              href={`/karte/${selectedPrefecture.pref_code_name}/lines/${line.line_name_alphabet}/${filterQuery}`}
                              className="text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919] underline-offset-[0.2rem] rf-hover-text"
                            >
                              {line.line_name}
                            </Link>
                          </>
                        ) : (
                          <>
                            <Checkbox className="lines_check" id={line.id} disabled />
                            <Typography className="opacity-60 text-[15px] font-medium leading-5 tracking-normal text-left text-[#191919] decoration-[#191919]">
                              {line.line_name}
                            </Typography>
                          </>
                        )}
                      </ListItem>
                    ))}
                </List>
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Box>
  );
}
